/**
 * Endpoint bindings — one function per documented route, no logic beyond
 * assembling params and normalising the response.
 */

import { apiRequest } from "./client";
import {
  decodeAttachment,
  decodeAttachmentList,
  decodeFolderCounts,
  decodeFolders,
  decodeHealth,
  decodeNote,
  decodeNotes,
  decodeSearchResponse,
  decodeTagCounts,
} from "./decode";
import type {
  Attachment,
  AttachmentList,
  FolderCount,
  HealthStatus,
  Note,
  NoteInput,
  NoteSummary,
  SearchQuery,
  SearchResponse,
  SearchSort,
  TagCount,
} from "./types";

export async function fetchHealth(signal?: AbortSignal): Promise<HealthStatus> {
  return decodeHealth(await apiRequest<unknown>("/api/health", signal === undefined ? {} : { signal }));
}

export async function fetchRecentNotes(limit = 25, signal?: AbortSignal): Promise<readonly NoteSummary[]> {
  // The server names this parameter `n`, not `limit` (max 100, default 5).
  return decodeNotes(
    await apiRequest<unknown>("/api/notes/recent", {
      params: { n: String(limit) },
      ...(signal === undefined ? {} : { signal }),
    }),
  );
}

export async function fetchNote(noteId: string, signal?: AbortSignal): Promise<Note> {
  return decodeNote(
    await apiRequest<unknown>(`/api/notes/${encodeURIComponent(noteId)}`, signal === undefined ? {} : { signal }),
  );
}

export async function createNote(input: NoteInput): Promise<Note> {
  return decodeNote(await apiRequest<unknown>("/api/notes", { method: "POST", body: input }));
}

export async function updateNote(noteId: string, input: NoteInput): Promise<Note> {
  return decodeNote(
    await apiRequest<unknown>(`/api/notes/${encodeURIComponent(noteId)}`, { method: "PUT", body: input }),
  );
}

export async function deleteNote(noteId: string): Promise<void> {
  await apiRequest<unknown>(`/api/notes/${encodeURIComponent(noteId)}`, { method: "DELETE" });
}

export async function fetchTags(signal?: AbortSignal): Promise<readonly TagCount[]> {
  return decodeTagCounts(await apiRequest<unknown>("/api/tags", signal === undefined ? {} : { signal }));
}

export async function fetchFolders(signal?: AbortSignal): Promise<readonly string[]> {
  return decodeFolders(await apiRequest<unknown>("/api/folders", signal === undefined ? {} : { signal }));
}

export async function fetchFolderCounts(signal?: AbortSignal): Promise<readonly FolderCount[]> {
  return decodeFolderCounts(await apiRequest<unknown>("/api/folders", signal === undefined ? {} : { signal }));
}

/**
 * The UI's two-way toggle expressed in the server's sort vocabulary.
 * The server accepts `_score`/`-_score`/`created`/`-created`/`updated`/`-updated`
 * and returns 400 for anything else, so this mapping is not optional.
 */
const SORT_PARAM: Readonly<Record<SearchSort, string>> = {
  relevance: "-_score",
  latest: "-updated",
};

export async function searchNotes(query: SearchQuery, signal?: AbortSignal): Promise<SearchResponse> {
  const params: Record<string, string> = { sort: SORT_PARAM[query.sort] };
  if (query.query !== "") {
    params["q"] = query.query;
  }
  if (query.facets !== "") {
    params["facets"] = query.facets;
  }
  if (query.tags.length > 0) {
    params["tags"] = query.tags.join(",");
  }
  if (query.folders.length > 0) {
    params["folders"] = query.folders.join(",");
  }
  if (query.limit !== undefined) {
    params["size"] = String(query.limit);
  }

  return decodeSearchResponse(
    await apiRequest<unknown>("/api/search", { params, ...(signal === undefined ? {} : { signal }) }),
  );
}

export async function fetchAttachments(noteId: string, signal?: AbortSignal): Promise<AttachmentList> {
  return decodeAttachmentList(
    await apiRequest<unknown>(
      `/api/notes/${encodeURIComponent(noteId)}/attachments`,
      signal === undefined ? {} : { signal },
    ),
  );
}

export async function uploadAttachment(noteId: string, file: File): Promise<Attachment> {
  return decodeAttachment(
    await apiRequest<unknown>(`/api/notes/${encodeURIComponent(noteId)}/attachments`, {
      method: "POST",
      params: { filename: file.name },
      file,
    }),
  );
}

export async function deleteAttachment(attachmentId: string): Promise<void> {
  await apiRequest<unknown>(`/api/attachments/${encodeURIComponent(attachmentId)}`, { method: "DELETE" });
}
